//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import _ from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Form, Icon, Modal, Table } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { AEUDiscount as ApiAEUDiscount, AEUDiscountList as ApiAEUDiscountList } from "../../api/billing/v1/ibilling";
import {
  ContentActionButtonNew,
  ErrorMessage,
  FormActionButtonCancel,
  FormActionButtonSave,
  ListActionDelete,
  Section,
  SectionButtons,
  SectionContent,
  SectionHead,
  SectionHeader,
} from "../../ui/lib";
import { hasSupportPermission } from "../../util/Permission";
import { DateTimePopupWithUTCAndLocalTime } from "../../util/dateAndTimeUtils/DateTime";
import { IWithRefreshProps, withRefresh } from "../../util/WithRefresh";

const dateFormat = "YYYY-MM-DD";

// The discount applies from the start of the given UTC day.
const startOfUTCDay = (date: string) => moment.utc(date, dateFormat).toDate();

// isFuture reports whether the entry starts in the future.
const isFuture = (x: ApiAEUDiscount) => !!x.starts_at && moment.utc(x.starts_at).isAfter(moment.utc());

// effectiveDiscountId returns the id of the entry that is effective now.
const effectiveDiscountId = (items: ApiAEUDiscount[]) => {
  const now = moment.utc();
  const effective = _.maxBy(
    items.filter((x) => !!x.starts_at && !moment.utc(x.starts_at).isAfter(now)),
    (x) => moment.utc(x.starts_at).valueOf()
  );
  return effective && effective.id;
};

interface IAEUDiscountTableArgs {
  items: ApiAEUDiscount[];
  canDelete: boolean;
  onDelete: (x: ApiAEUDiscount) => void;
}

const AEUDiscountTable = ({ items, canDelete, onDelete }: IAEUDiscountTableArgs) => {
  if (_.isEmpty(items)) {
    return <div>No AEU discount (AEU is charged at the list price)</div>;
  }
  const effectiveId = effectiveDiscountId(items);
  return (
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Discount</Table.HeaderCell>
          <Table.HeaderCell>Starts (UTC)</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.HeaderCell>Description</Table.HeaderCell>
          <Table.HeaderCell>Created</Table.HeaderCell>
          <Table.HeaderCell>Created by</Table.HeaderCell>
          <Table.HeaderCell />
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {items.map((x) => (
          <Table.Row key={`aeu-discount-${x.id}`} positive={x.id === effectiveId}>
            <Table.Cell>{x.discount_percent || 0}%</Table.Cell>
            <Table.Cell>{x.starts_at ? moment.utc(x.starts_at).format(dateFormat) : "-"}</Table.Cell>
            <Table.Cell>{x.id === effectiveId ? "Effective" : isFuture(x) ? "Scheduled" : "Past"}</Table.Cell>
            <Table.Cell>{x.description || "-"}</Table.Cell>
            <Table.Cell>
              <DateTimePopupWithUTCAndLocalTime dateTime={x.created_at} label="Created at" />
            </Table.Cell>
            <Table.Cell>{x.created_by_id || "-"}</Table.Cell>
            <Table.Cell textAlign="right">{canDelete && <ListActionDelete onClick={() => onDelete(x)} />}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

interface IAEUDiscountEditorArgs {
  organization_id?: string;
  onCancel: () => void;
  onSuccess: () => void;
}

const AEUDiscountEditor = ({ organization_id, onCancel, onSuccess }: IAEUDiscountEditorArgs) => {
  const today = moment.utc().format(dateFormat);
  const [percent, setPercent] = useState<string>("0");
  const [startsAt, setStartsAt] = useState<string>(today);
  const [description, setDescription] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const value = Number(percent);
  const isValid = percent !== "" && !isNaN(value) && value >= 0 && value <= 100 && !!startsAt;
  const onSave = async () => {
    try {
      const req: ApiAEUDiscount = {
        organization_id,
        discount_percent: value,
        starts_at: startOfUTCDay(startsAt),
        description,
      };
      await apiClients.idashboardClient.CreateAEUDiscount(req);
      onSuccess();
    } catch (e) {
      setErrorMessage(e.message);
    }
  };
  return (
    <Modal open={true} size="small">
      <Modal.Header>
        <Icon name="percent" />
        New AEU discount
      </Modal.Header>
      <Modal.Content>
        <ErrorMessage active={!!errorMessage} onDismiss={() => setErrorMessage("")} message={errorMessage} />
        <Form>
          <Form.Input required label="Discount (%)" type="number" min={0} max={100} step="0.01" value={percent} onChange={(e, d) => setPercent(d.value)} />
          <Form.Input required label="Effective from (UTC day)" type="date" value={startsAt} onChange={(e, d) => setStartsAt(d.value)} />
          <Form.Input label="Description (e.g. contract reference)" value={description} onChange={(e, d) => setDescription(d.value)} />
        </Form>
        <p>
          The discount applies to AEU and Dedicated Data Cluster charges only; infrastructure is never discounted. It replaces the current discount from the
          selected day on. The day may lie in the past (e.g. a contract effective before the organization moved to Everest), but usage that is already ingested
          into Everest is not re-priced.
        </p>
      </Modal.Content>
      <Modal.Actions>
        <FormActionButtonCancel onClick={onCancel} />
        <FormActionButtonSave onClick={onSave} primary disabled={!isValid} />
      </Modal.Actions>
    </Modal>
  );
};

interface IAEUDiscountsProps {
  organization_id?: string;
  // Injected by withRefresh.
  refreshNow?: IWithRefreshProps["refreshNow"];
  error?: IWithRefreshProps["error"];
  hasPermissionByUrl?: IWithRefreshProps["hasPermissionByUrl"];
}

// AEUDiscounts shows the AEU discount history of an organization and lets
// billing staff schedule a new discount (e.g. for a renewed contract).
const AEUDiscounts = ({ organization_id, refreshNow, hasPermissionByUrl, error }: IAEUDiscountsProps) => {
  // Undefined until the history has been loaded successfully.
  const [discounts, setDiscounts] = useState<ApiAEUDiscount[]>();
  const [showEditor, setShowEditor] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const canEdit = hasSupportPermission("internal-dashboard.organization.set-billing-v2-enabled", hasPermissionByUrl);

  // Load the complete history (all pages): the effective entry is only known once
  // every entry has been seen.
  const reloadDiscounts = async () => {
    const pageSize = 50;
    let items: ApiAEUDiscount[] = [];
    for (let page = 0; ; page++) {
      const list: ApiAEUDiscountList = await apiClients.idashboardClient.ListAEUDiscounts({
        organization_id,
        options: { page, page_size: pageSize },
      });
      const pageItems = list.items || [];
      items = items.concat(pageItems);
      if (pageItems.length < pageSize) {
        break;
      }
    }
    setDiscounts(items);
  };
  const refreshDiscounts = () => {
    refreshNow && refreshNow(reloadDiscounts);
  };
  useEffect(() => {
    refreshDiscounts();
  }, []);

  const onDelete = async (x: ApiAEUDiscount) => {
    if (
      !window.confirm(
        `Delete the ${x.discount_percent || 0}% AEU discount effective from ${moment
          .utc(x.starts_at)
          .format(dateFormat)}? Usage already ingested into Everest is not re-priced.`
      )
    ) {
      return;
    }
    try {
      await apiClients.idashboardClient.DeleteAEUDiscount({ id: x.id });
      await reloadDiscounts();
    } catch (e) {
      setErrorMessage(e.message);
    }
  };

  return (
    <Section>
      <SectionHead>
        <SectionHeader title="AEU discount (Billing 2.0)" />
        <SectionButtons>{canEdit && <ContentActionButtonNew primary content="New discount" onClick={() => setShowEditor(true)} />}</SectionButtons>
      </SectionHead>
      <SectionContent>
        <ErrorMessage active={!!errorMessage} onDismiss={() => setErrorMessage("")} message={errorMessage} />
        {discounts === undefined ? (
          <div>{error ? "The AEU discount history could not be loaded." : "Loading AEU discount history..."}</div>
        ) : (
          <AEUDiscountTable items={discounts} canDelete={canEdit} onDelete={onDelete} />
        )}
      </SectionContent>
      {showEditor && (
        <AEUDiscountEditor
          organization_id={organization_id}
          onCancel={() => setShowEditor(false)}
          onSuccess={() => {
            setShowEditor(false);
            reloadDiscounts().catch((e) => setErrorMessage(e.message));
          }}
        />
      )}
    </Section>
  );
};

export default withRefresh()(AEUDiscounts);
